import { Link } from "react-router-dom";
import { Lock, BookOpen, FileWarning, ShoppingCart, BookMarked } from "lucide-react";
import { RedactedText, RedactionLines } from "@/components/redaction/RedactedText";
import { FragmentProbe } from "@/components/secrets/FragmentProbe";
import { useSecrets } from "@/context/SecretsContext";
import { useStore } from "@/context/StoreContext";
import { SITE } from "@/data/series";

const statusClass = (status) => {
  if (status === "DECLASSIFIED") return "stamp stamp-open";
  if (status === "CLASSIFIED") return "stamp";
  return "stamp stamp-neutral";
};

export const BookCard = ({ book }) => {
  const hard = Boolean(book.hardLocked);
  const { isEggActive, markEggFound, openOverlay } = useSecrets();
  const { code, kindleUrl, paperbackUrl, urlFor } = useStore();
  const dedicationEgg = hard && isEggActive("dedication");
  const listingUrl = book.asin ? urlFor(book.asin) : kindleUrl;

  return (
    <article
      data-testid={`book-card-${book.number}`}
      tabIndex={0}
      className="group sweep relative flex h-full flex-col overflow-hidden rounded-[var(--radius)] border border-border bg-card shadow-[0_20px_60px_rgba(0,0,0,0.38)] transition-colors duration-200 hover:border-[hsl(174_72%_42%/0.45)] focus-visible:border-[hsl(174_72%_42%/0.6)]"
    >
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 sm:px-5">
        <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          Book {book.numeral}
        </span>
        <span
          className={statusClass(book.status)}
          data-testid={`book-status-${book.number}`}
        >
          {book.status}
        </span>
      </header>

      <div className="flex flex-1 flex-col px-4 py-5 sm:px-5">
        <h3 className="text-xl font-semibold leading-tight sm:text-[1.35rem]">
          {book.title ? (
            book.title
          ) : (
            <FragmentProbe
              fragmentId="f3"
              label="Probe the withheld title"
              className="inline-flex items-center gap-2 text-muted-foreground"
            >
              <span className="redact px-1">████████████</span>
            </FragmentProbe>
          )}
        </h3>

        {book.epigraph ? (
          <p className="mt-2 text-sm italic text-[hsl(32_60%_74%)]">
            {book.epigraph}
          </p>
        ) : dedicationEgg ? (
          <button
            type="button"
            onClick={() => {
              markEggFound("dedication");
              openOverlay({ kind: "dedication" });
            }}
            data-testid="book-six-dedication-egg"
            className="mt-2 cursor-crosshair font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground transition-colors duration-150 hover:text-[hsl(32_80%_66%)]"
          >
            Dedication withheld
          </button>
        ) : (
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
            Dedication withheld
          </p>
        )}

        <div className="mt-4 border-t border-border pt-4">
          <p className="eyebrow">Point of view</p>
          <p className="mt-1.5 font-mono text-[11px] leading-relaxed text-[hsl(210_14%_78%)]">
            <RedactedText text={book.pov} />
          </p>
        </div>

        <div className="mt-4 flex-1">
          <p className="eyebrow">
            {book.locked ? "Recovered fragment" : "Case summary"}
          </p>

          {hard ? (
            <div className="mt-2.5">
              <RedactionLines widths={["94%", "81%", "88%", "46%"]} />
              <p className="mt-3 flex items-start gap-2 font-mono text-[11px] leading-relaxed text-[hsl(2_70%_66%)]">
                <FileWarning className="mt-[1px] h-3.5 w-3.5 shrink-0" />
                <RedactedText text={book.note} />
              </p>
            </div>
          ) : (
            <p
              className={`mt-2.5 text-sm leading-relaxed text-[hsl(210_14%_82%)] ${
                book.locked ? "fragment-veil" : ""
              }`}
            >
              <RedactedText text={book.fragment} />
            </p>
          )}
        </div>

        <footer className="mt-5 flex items-center justify-between gap-3 border-t border-border pt-4">
          <span
            className={`font-mono text-[11px] uppercase tracking-[0.14em] ${
              book.released
                ? "text-[hsl(174_62%_64%)]"
                : "text-muted-foreground"
            }`}
            data-testid={`book-release-${book.number}`}
          >
            {book.releaseDate || SITE.dateplaceholder}
          </span>

          {book.released ? (
            book.singleListing ? (
              <a
                href={listingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`book-buy-link-${book.number}`}
                className="inline-flex items-center gap-2 rounded-[10px] bg-primary px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-primary-foreground transition-colors duration-150 hover:bg-[hsl(174_72%_36%)]"
              >
                <ShoppingCart className="h-3.5 w-3.5" />
                Kindle &amp; paper
                <span aria-hidden="true" className="text-primary-foreground/70">
                  {code}
                </span>
              </a>
            ) : (
            <span className="flex items-center gap-2">
              <a
                href={kindleUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`book-buy-link-${book.number}`}
                className="inline-flex items-center gap-2 rounded-[10px] bg-primary px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-primary-foreground transition-colors duration-150 hover:bg-[hsl(174_72%_36%)]"
              >
                <ShoppingCart className="h-3.5 w-3.5" />
                Kindle
                <span aria-hidden="true" className="text-primary-foreground/70">
                  {code}
                </span>
              </a>
              <a
                href={paperbackUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`book-paperback-link-${book.number}`}
                className="inline-flex items-center gap-2 rounded-[10px] border border-[hsl(32_60%_50%/0.45)] px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(32_92%_66%)] transition-colors duration-150 hover:bg-[hsl(32_60%_40%/0.12)]"
              >
                <BookMarked className="h-3.5 w-3.5" />
                Paper
              </a>
            </span>
            )
          ) : (
            <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(215_10%_50%)]">
              <Lock className="h-3.5 w-3.5" />
              {hard ? "No access" : "Sealed"}
            </span>
          )}
        </footer>

        {book.excerptAvailable && (
          <Link
            to="/read"
            data-testid={`book-read-link-${book.number}`}
            className="mt-3 inline-flex items-center gap-2 self-start rounded-[10px] border border-[hsl(174_72%_42%/0.5)] bg-[hsl(174_40%_40%/0.1)] px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-[hsl(174_62%_66%)] transition-colors duration-150 hover:bg-[hsl(174_40%_40%/0.2)]"
          >
            <BookOpen className="h-3.5 w-3.5" />
            Read the opening free
          </Link>
        )}
      </div>

      {book.locked && !hard && (
        <span className="pointer-events-none absolute bottom-[4.4rem] left-4 font-mono text-[10px] uppercase tracking-[0.16em] text-[hsl(215_10%_45%)] opacity-100 transition-opacity duration-200 group-hover:opacity-0 group-focus-within:opacity-0 sm:left-5">
            hover to decrypt
        </span>
      )}
    </article>
  );
};
